import { reminderConfig } from "definitions/constants/config";
import { Task } from "./models";

// Re-export for backward compatibility - prefer importing from config directly
export const RECENTLY_REMINDED_WINDOW_HOURS = reminderConfig.recentlyRemindedWindowHours;

/** Individual task status constants for type safety */
export const TaskStatus = {
  UPCOMING: "upcoming",
  ACTIVE: "active",
  IN_PROGRESS: "in_progress",
  BLOCKED: "blocked",
  COMPLETED: "completed",
  SKIPPED: "skipped",
  FAILED: "failed",
  REMOVED: "removed",
  CLOSED_STALE: "closed_stale",
} as const;

export type TaskStatusValue = (typeof TaskStatus)[keyof typeof TaskStatus];

export const ACTIVE_TASK_STATUSES: Task["status"][] = [
  TaskStatus.ACTIVE,
  TaskStatus.IN_PROGRESS,
  TaskStatus.BLOCKED,
  TaskStatus.FAILED,
];

/** Tasks that are in progress but not yet completed or failed */
export const INCOMPLETE_TASK_STATUSES: Task["status"][] = [
  TaskStatus.ACTIVE,
  TaskStatus.IN_PROGRESS,
  TaskStatus.BLOCKED,
];

export const NOT_COUNTED_TASK_STATUSES = ["removed", "skipped", "closed_stale"];

export const NOT_CLOSED_NOT_FAILED_TASK_STATUSES = ["upcoming", "active", "in_progress", "blocked"];

export const NOT_CLOSED_TASK_STATUSES = ["upcoming", "active", "in_progress", "blocked", "failed"];

export const CLOSED_NOT_ERRORED_TASK_STATUSES = ["skipped", "completed"];

/** All closed task statuses including removed and closed_stale */
export const CLOSED_TASK_STATUSES: Task["status"][] = [
  TaskStatus.COMPLETED,
  TaskStatus.SKIPPED,
  TaskStatus.REMOVED,
  TaskStatus.CLOSED_STALE,
];

/** Open task statuses - tasks that are actionable (not closed, not upcoming) */
export const OPEN_TASK_STATUSES: Task["status"][] = [
  TaskStatus.ACTIVE,
  TaskStatus.IN_PROGRESS,
  TaskStatus.BLOCKED,
  TaskStatus.FAILED,
];

export const NO_ACTION_STATUSES = ["upcoming", "skipped", "completed"];

export const TASK_STATUSES_INCLUDED_IN_COMPLETION_RATE = ["upcoming", "active", "in_progress", "blocked", "completed"];

export const TASK_STATUSES = new Map([
  ["upcoming", { colors: "content.tertiary", label: "Not Started", value: "upcoming" }],
  ["active", { colors: "dateBlue.500", label: "Active", value: "active" }],
  ["in_progress", { colors: "dateBlue.500", label: "In Progress", value: "in_progress" }],
  ["blocked", { colors: "cdsOrange.500", label: "Blocked", value: "blocked" }],
  ["completed", { colors: "cdsGreen.500", label: "Completed", value: "completed" }],
  ["skipped", { colors: "content.tertiary", label: "Skipped", value: "skipped" }],
  ["removed", { colors: "content.tertiary", label: "Removed", value: "removed" }],
  ["closed_stale", { colors: "content.tertiary", label: "Closed (stale)", value: "closed_stale" }],
  ["failed", { colors: "statusRed.text", label: "Failed", value: "failed" }],
]);

export type TASK_STATUS_FILTER_VALUE = "upcoming" | "urgent" | "in_progress" | "closed" | "failed";

export type TASK_STATUS_FILTER_OPTION = {
  label: string;
  value: TASK_STATUS_FILTER_VALUE;
  filterFn: (task: Task) => boolean;
};

const ENTRIES = [
  ["upcoming", { label: "Not Started", value: "upcoming", filterFn: (task: Task) => task?.status === "upcoming" }],
  ["urgent", { label: "Urgent", value: "urgent", filterFn: (task: Task) => !!task?.isUrgent }],
  [
    "in_progress",
    {
      label: "In Progress",
      value: "in_progress",
      filterFn: (task: Task) => ["active", "in_progress", "blocked"].includes(task?.status),
    },
  ],
  [
    "closed",
    { label: "Completed", value: "closed", filterFn: (task: Task) => ["completed", "skipped"].includes(task?.status) },
  ],
  ["failed", { label: "Failed", value: "failed", filterFn: (task: Task) => task?.status === "failed" }],
] as const satisfies ReadonlyArray<readonly [TASK_STATUS_FILTER_VALUE, TASK_STATUS_FILTER_OPTION]>;

export const TASK_STATUS_FILTERS = new Map<TASK_STATUS_FILTER_VALUE, TASK_STATUS_FILTER_OPTION>(ENTRIES);
